if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoom = 1;
        this.content = this.querySelector('[role="document"]');
        this.zoomControls = this.querySelector('.maxsleek-image-zoom');
        this.navigation = this.querySelector('.maxsleek-image-navigation');
        this.pointers = new Set();
        this.onImageResize = () => this.sizeImage();
        // Dawn closes media modals on mouse pointerup. Keep image inspection and
        // the explicit controls open, without changing video/model behaviour.
        [this.content, this.zoomControls, this.navigation].filter(Boolean).forEach((element) => {
          element.addEventListener('pointerup', (event) => {
            if (this.activeImage) event.stopPropagation();
          });
        });
        this.zoomControls?.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-image-zoom]');
          if (!button || !this.zoomControls.contains(button) || !this.activeImage) return;
          const action = button.dataset.imageZoom;
          this.zoom = action === 'fit' ? 1 : Math.max(1, Math.min(6, this.zoom + (action === 'in' ? 0.5 : -0.5)));
          this.swipe = null;
          this.sizeImage(true);
        });
        this.navigation?.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-image-step]');
          if (button && this.navigation.contains(button)) this.navigateImage(Number(button.dataset.imageStep));
        });
        this.addEventListener('keydown', (event) => {
          if (!this.activeImage || !this.hasAttribute('open') || this.zoom !== 1 || this.pageIsZoomed() ||
              event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.isContentEditable ||
              /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.navigateImage(event.key === 'ArrowRight' ? 1 : -1);
          }
        });
        this.content?.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch') return;
          this.pointers.add(event.pointerId);
          this.swipe = this.pointers.size === 1 && this.activeImage && this.zoom === 1 && !this.pageIsZoomed()
            ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
        }, { passive: true });
        this.content?.addEventListener('pointermove', (event) => {
          if (this.swipe && (this.pointers.size !== 1 || Math.abs(event.clientY - this.swipe.y) > 30)) this.swipe = null;
        }, { passive: true });
        this.content?.addEventListener('pointerup', (event) => {
          const swipe = this.swipe;
          this.swipe = null;
          this.pointers.delete(event.pointerId);
          if (!swipe || swipe.id !== event.pointerId || this.zoom !== 1 || this.pageIsZoomed()) return;
          const dx = event.clientX - swipe.x;
          const dy = event.clientY - swipe.y;
          if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && Math.abs(dy) <= 30) {
            this.navigateImage(dx < 0 ? 1 : -1);
          }
        }, { passive: true });
        this.content?.addEventListener('pointercancel', (event) => {
          this.pointers.delete(event.pointerId);
          this.swipe = null;
        }, { passive: true });
      }

      pageIsZoomed() {
        return window.visualViewport && window.visualViewport.scale > 1.01;
      }

      imageItems() {
        return Array.from(this.content?.children || []).filter((element) => element.tagName === 'IMG');
      }

      updateNavigation() {
        if (!this.navigation) return;
        const images = this.imageItems();
        this.navigation.hidden = !this.activeImage || images.length < 2;
        this.navigation.querySelector('[data-image-position]').textContent = `${images.indexOf(this.activeImage) + 1} / ${images.length}`;
      }

      navigateImage(step) {
        if (!this.activeImage || !this.hasAttribute('open')) return;
        const images = this.imageItems();
        if (images.length < 2) return;
        const next = images[(images.indexOf(this.activeImage) + step + images.length) % images.length];
        this.releaseImage();
        this.activeImage = next;
        this.classList.add('maxsleek-image-view');
        this.zoomControls.hidden = false;
        this.showActiveMedia();
      }

      hide() {
        this.releaseImage();
        super.hide();
      }

      disconnectedCallback() {
        this.releaseImage();
      }

      releaseImage() {
        this.swipe = null;
        this.pointers.clear();
        this.content?.style.removeProperty('touch-action');
        if (this.navigation) this.navigation.hidden = true;
        window.removeEventListener('resize', this.onImageResize);
        if (this.activeImage) {
          this.activeImage.removeEventListener('load', this.onImageResize);
          ['width', 'height', 'margin-top'].forEach((property) => this.activeImage.style.removeProperty(property));
        }
        this.activeImage = null;
        this.zoom = 1;
        this.classList.remove('maxsleek-image-view');
        if (this.zoomControls) this.zoomControls.hidden = true;
      }

      show(opener) {
        this.releaseImage();
        this.openedBy = opener;
        const activeMedia = this.getActiveMedia();
        if (!activeMedia) return;
        if (activeMedia.tagName === 'IMG' && this.zoomControls) {
          this.activeImage = activeMedia;
          this.classList.add('maxsleek-image-view');
          this.zoomControls.hidden = false;
        }
        super.show(opener);
        this.showActiveMedia();
      }

      getActiveMedia() {
        if (this.activeImage) return this.activeImage;
        const id = this.openedBy?.getAttribute('data-media-id');
        if (!this.content || !id) return null;
        return Array.from(this.content.children).find((element) => element.getAttribute('data-media-id') === id);
      }

      sizeImage(center = false) {
        if (!this.activeImage || !this.hasAttribute('open')) return;
        this.content.style.touchAction = this.zoom === 1 ? 'pan-y pinch-zoom' : 'auto';
        const image = this.activeImage;
        const width = image.naturalWidth || Number(image.getAttribute('width'));
        const height = image.naturalHeight || Number(image.getAttribute('height'));
        if (!width || !height || !this.content.clientWidth || !this.content.clientHeight) return;
        const fit = Math.min(this.content.clientWidth / width, this.content.clientHeight / height, 1);
        const displayWidth = Math.floor(width * fit * this.zoom);
        const displayHeight = Math.floor(height * fit * this.zoom);
        image.style.width = `${displayWidth}px`;
        image.style.height = `${displayHeight}px`;
        image.style.marginTop = `${Math.max(0, (this.content.clientHeight - displayHeight) / 2)}px`;
        if (center || this.zoom === 1) {
          this.content.scrollLeft = Math.max(0, (displayWidth - this.content.clientWidth) / 2);
          this.content.scrollTop = Math.max(0, (displayHeight - this.content.clientHeight) / 2);
        }
        this.zoomControls.querySelector('[data-image-zoom-status]').textContent = `${Math.round(this.zoom * 100)}%`;
        this.zoomControls.querySelector('[data-image-zoom="out"]').setAttribute('aria-disabled', String(this.zoom === 1));
        this.zoomControls.querySelector('[data-image-zoom="in"]').setAttribute('aria-disabled', String(this.zoom === 6));
      }

      showActiveMedia() {
        const activeMedia = this.getActiveMedia();
        if (!activeMedia) return;
        Array.from(this.content.children).forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });
        if (this.activeImage) {
          this.updateNavigation();
          this.sizeImage(true);
          this.activeImage.addEventListener('load', this.onImageResize);
          window.addEventListener('resize', this.onImageResize);
          return;
        }
        // Keep Dawn's existing deferred video/model flow.
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.scrollIntoView();
        this.content.scrollLeft = (activeMedia.width - this.content.clientWidth) / 2;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) activeMedia.loadContent();
      }
    }
  );
}
